import React, { useCallback, useEffect, useState } from 'react';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useCart } from 'hooks/use-cart';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { isMobile } from 'utils/environment';

import { LoyaltyRewardsCategoryCarousel } from './loyalty-rewards-category-carousel';
import { LoyaltyRewardsCategoryList } from './loyalty-rewards-category-list';
import { LoyaltyRewardsMeter } from './loyalty-rewards-meter';
import {
  CategoryLabel,
  CrownIcon,
  RewardCategoryContainer,
  RewardCategoryHeader,
} from './loyalty-rewards.styled';
import { ILoyaltyRewardsCategory } from './types';
import { useAppliedRewards } from './use-applied-reward';

export const LoyaltyRewardsCategory = ({
  rewards,
  category,
  firstUnavailableCategory,
  engineRewardsMap,
  handleOnPress,
}: ILoyaltyRewardsCategory) => {
  const { cartEntries, removeFromCart } = useCart();
  const [unavailableCartEntries, setUnavailableCartEntries] = useState<ILegacyCartEntry[]>([]);
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const appliedRewards = useAppliedRewards();
  const { rewardsCooldown } = useLoyaltyContext();
  const { isAuthenticated } = useAuthContext();

  // Only show carousel if on Desktop web
  const showCarousel = !isMobile();

  const categoryLabel = category.label?.en;

  const handleRemoveUnavailableItems = useCallback(() => {
    unavailableCartEntries.forEach(entry => {
      removeFromCart({ cartId: entry.cartId });
    });
    setUnavailableCartEntries([]);
  }, [removeFromCart, unavailableCartEntries]);

  useEffect(() => {
    if (!enableRewardUnstacking) {
      setUnavailableCartEntries([]);
      return;
    }

    // Build the set of reward IDs in *this category* that are specifically unavailable
    // (NOT_AVAILABLE_IN_STORE). Other evaluation results (e.g. wrong service mode, cooldown)
    // are fixable and should not trigger removal.
    const unavailableRewardIds = new Set(
      rewards
        .filter(enrichedReward =>
          enrichedReward.rewardEvaluationResults?.some(
            result => result.code === IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE
          )
        )
        .map(enrichedReward => enrichedReward.rewardId)
    );

    if (unavailableRewardIds.size === 0) {
      setUnavailableCartEntries([]);
      return;
    }

    // Only queue applied rewards that belong to this category AND are unavailable.
    const next: ILegacyCartEntry[] = [];
    for (const applied of appliedRewards) {
      if (!unavailableRewardIds.has(applied.rewardId)) {
        continue;
      }
      const cartEntry = cartEntries.find(entry => entry.cartId === applied.cartId);
      if (cartEntry) {
        next.push(cartEntry);
      }
    }
    setUnavailableCartEntries(next);
  }, [rewards, enableRewardUnstacking, appliedRewards, cartEntries]);

  const categoryHeaderAccessibilityHint = `${categoryLabel} ${t('crowns')}`;

  const engineRewardItem = engineRewardsMap[rewards[0].reward.loyaltyEngineId || ''];
  const { locked, redemptionPercentage, remainingPointsNeededForRedemption } = engineRewardItem;
  const showMeter = locked && firstUnavailableCategory?.label?.en === categoryLabel;

  return (
    <>
      {showMeter ? (
        <LoyaltyRewardsMeter
          redemptionPercentage={redemptionPercentage || 0}
          remainingPointsNeededForRedemption={remainingPointsNeededForRedemption || undefined}
        ></LoyaltyRewardsMeter>
      ) : null}

      <RewardCategoryContainer locked={locked || false} accessible={false}>
        <RewardCategoryHeader>
          <CrownIcon />
          <CategoryLabel accessibilityLabel={categoryHeaderAccessibilityHint}>
            {categoryLabel}
          </CategoryLabel>
        </RewardCategoryHeader>

        {showCarousel ? (
          <LoyaltyRewardsCategoryCarousel
            rewards={rewards}
            locked={!isAuthenticated || locked || false}
            handleOnPress={handleOnPress}
            inCooldown={rewardsCooldown.isActive}
          />
        ) : (
          <LoyaltyRewardsCategoryList
            rewards={rewards}
            locked={!isAuthenticated || locked || false}
            handleOnPress={handleOnPress}
            inCooldown={rewardsCooldown.isActive}
          />
        )}
      </RewardCategoryContainer>

      {unavailableCartEntries.length > 0 && (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleRemoveUnavailableItems}
          unavailableCartEntries={unavailableCartEntries}
        />
      )}
    </>
  );
};
